import { stripInstallmentSuffix } from './installmentRebase';

export type AutoCategorizationTargetType = 'expense' | 'income';

export type MatchableAutoCategorizationRule = {
    id: string;
    matchPattern: string;
    priority: number;
    categoryType?: AutoCategorizationTargetType;
};

export type AutoCategorizationRuleMatch<T extends MatchableAutoCategorizationRule> = {
    rule: T;
    matchedBranch: string;
    normalizedDescription: string;
    normalizedPattern: string;
    specificity: number;
};

const COMBINING_MARKS_REGEX = /[\u0300-\u036f]/g;
const DESCRIPTION_NOISE_REGEX = /[^A-Z0-9]+/g;
const PATTERN_NOISE_REGEX = /[^A-Z0-9*|]+/g;
const MULTISPACE_REGEX = /\s+/g;
const REGEX_META_REGEX = /[.*+?^${}()|[\]\\]/g;
const DIGITS_ONLY_REGEX = /^\d+$/;
const IFOOD_SHORT_PREFIX_REGEX = /(^|[\s|*])IFD(?=$|[\s|*])/g;

const CONNECTOR_TOKENS = new Set([
    'A',
    'AS',
    'COM',
    'DA',
    'DAS',
    'DE',
    'DO',
    'DOS',
    'E',
    'EM',
    'NA',
    'NAS',
    'NO',
    'NOS',
    'O',
    'OS',
    'PARA',
    'POR',
    'PRA',
    'PRO',
    'SEM',
    'VIA',
]);

const LOW_SIGNAL_TOKENS = new Set([
    'AJUSTE',
    'BANCO',
    'BOLETO',
    'CARTAO',
    'COMPRA',
    'CONTA',
    'CREDITO',
    'DEBITO',
    'DOC',
    'ENVIADA',
    'ENVIADO',
    'FATURA',
    'LANCAMENTO',
    'PAGAMENTO',
    'PAGO',
    'PARCELA',
    'PARCELAMENTO',
    'PIX',
    'RECEBIDA',
    'RECEBIDO',
    'SALDO',
    'TED',
    'TRANSFERENCIA',
]);

/**
 * Marketplaces e intermediadores cujo nome não diz o que foi comprado. Um ramo de
 * regra cujo prefixo literal é MAIS CURTO que o nome deles (o `MERCADO*` de fábrica,
 * pensado pra supermercado) casava "MERCADOLIVRE*..." e "MERCADO PAGO ..." como
 * Supermercado — 21 linhas em 28 faturas de teste e 3 do histórico real errando
 * assim (avaliação de set/2026). A regra que nomeia o marketplace inteiro continua
 * valendo, inclusive a aprendida (`MERCADO*LIVRE*`): compara-se o texto literal do
 * ramo, sem espaço nem curinga.
 */
const MARKETPLACE_NAMES = ['MERCADOLIVRE', 'MERCADOPAGO'] as const;

const compact = (value: string) => value.replace(/[\s*]/g, '');

function cutsThroughMarketplace(branch: string, normalizedDescription: string): boolean {
    // Ramo que começa com `*` é "contém": o usuário escreveu o trecho que importa.
    if (branch.startsWith('*')) return false;
    const description = compact(normalizedDescription);
    const literal = compact(branch);
    return MARKETPLACE_NAMES.some((name) => description.startsWith(name) && !literal.startsWith(name));
}

/**
 * Marketplaces e intermediadores de pagamento, como aparecem no começo da descrição
 * ("SHOPEE *LOJA", "MERCADOLIVRE*VENDEDOR", "MP *PADARIA", "PAG*LOJA"). O nome diz
 * ONDE se pagou, não O QUE se comprou: sozinho ele não ensina categoria. Uma compra
 * "Shopee.SHOPEE.E" ensinou `SHOPEE*` → Pets, e a regra errou 13 de 15 compras
 * seguintes (avaliação de set/2026). Quem ensina é o nome da loja que vem depois.
 */
const MARKETPLACE_TOKENS = new Set([
    'ALIEXPRESS',
    'AMAZON',
    'AMAZONMKTPLC',
    'AMERICANAS',
    'AMZN',
    'AMZNMKTP',
    'EBANX',
    'MAGALU',
    'MERCADOLIVRE',
    'MERCADOPAGO',
    'MKTP',
    'MP',
    'PAG',
    'PAGSEGURO',
    'PAYPAL',
    'PICPAY',
    'SHEIN',
    'SHOPEE',
    'SHPP',
    'TEMU',
]);
/** "MERCADO LIVRE"/"MERCADO PAGO" com espaço também é o marketplace. */
const MARKETPLACE_SECOND_TOKENS: Record<string, ReadonlySet<string>> = {
    MERCADO: new Set(['LIVRE', 'PAGO']),
};
/** Nome de loja com menos letras que isso é fatia truncada da fatura ("MERCADOLIVRE*MU"). */
const MIN_SELLER_TOKEN_LENGTH = 4;

/** Quantos tokens do começo formam o nome do marketplace (0 = não começa com um). */
function marketplacePrefixLength(tokens: string[]): number {
    const [first, second] = tokens;
    if (!first) return 0;
    if (MARKETPLACE_TOKENS.has(first)) return 1;
    if (second && MARKETPLACE_SECOND_TOKENS[first]?.has(second)) return 2;
    return 0;
}

/** Tamanho a partir do qual um amontoado de letra com número é código, não nome. */
const MIN_CODE_TOKEN_LENGTH = 5;
/** Quantas trocas letra↔número denunciam código ("2A3B4C5D6") e não nome ("123SEGU"). */
const MIN_CODE_TOKEN_SWITCHES = 3;
const VOWEL_REGEX = /[AEIOU]/;
const DIGIT_REGEX = /\d/;

/** Quantas vezes o token alterna entre letra e número. */
function countDigitLetterSwitches(token: string): number {
    let switches = 0;
    for (let i = 1; i < token.length; i++) {
        if (DIGIT_REGEX.test(token[i]) !== DIGIT_REGEX.test(token[i - 1])) switches++;
    }
    return switches;
}

/**
 * Token que é CÓDIGO de transação/reserva, não nome de ninguém: o `HMCT8N` de
 * "AIRBNB . HMCT8N", o `2X9Y8Z` de "Amazon.com.br*2X9Y8Z". Ele muda a cada compra, e
 * por isso envenena as duas pontas: entra no padrão aprendido e cria regra que nunca
 * mais casa (`AIRBNB*HMCT8N*`), e entra na comparação por tokens roubando o peso da
 * marca — sendo único no histórico, é o token mais raro de todos no IDF.
 *
 * Reconhecido pelo que NÃO parece nome: número puro longo, ou tem dígito e ou não tem
 * vogal nenhuma ("HMCT8N", "RT4H92KL3") ou intercala letra e número várias vezes
 * ("2A3B4C5D6", "6OYYX1MJ2Q"). Loja de verdade com número no nome passa ilesa:
 * "123SEGU" tem vogal e uma única troca. O mínimo de 5 caracteres protege "K9" e "C6".
 */
export function isCodeToken(token: string): boolean {
    if (token.length < MIN_CODE_TOKEN_LENGTH) return false;
    if (DIGITS_ONLY_REGEX.test(token)) return true;
    if (!DIGIT_REGEX.test(token)) return false;
    if (!VOWEL_REGEX.test(token)) return true;
    return countDigitLetterSwitches(token) >= MIN_CODE_TOKEN_SWITCHES;
}

/** Token que pode ser o nome da loja depois do marketplace ("SHOPEE *TRESESQ"). */
function isSellerToken(token: string): boolean {
    return token.length >= MIN_SELLER_TOKEN_LENGTH
        && !DIGITS_ONLY_REGEX.test(token)
        && !isCodeToken(token)
        && !CONNECTOR_TOKENS.has(token)
        && !LOW_SIGNAL_TOKENS.has(token)
        && marketplacePrefixLength([token]) === 0;
}

/**
 * Descritores diferentes da mesma casa. Sem isso, "AMZN Mktp BR" e "Amazon.com.br"
 * contariam como dois marketplaces no consenso do histórico (ADR 0143).
 */
const MARKETPLACE_ALIASES: Record<string, string> = {
    AMZN: 'AMAZON',
    AMZNMKTP: 'AMAZON',
    AMAZONMKTPLC: 'AMAZON',
    SHPP: 'SHOPEE',
    MP: 'MERCADOPAGO',
    PAG: 'PAGSEGURO',
};

/** Como o nome do marketplace é escrito pra pessoa ler. */
const MARKETPLACE_DISPLAY_NAMES: Record<string, string> = {
    ALIEXPRESS: 'AliExpress',
    AMAZON: 'Amazon',
    AMERICANAS: 'Americanas',
    EBANX: 'Ebanx',
    MAGALU: 'Magalu',
    MERCADOLIVRE: 'Mercado Livre',
    MERCADOPAGO: 'Mercado Pago',
    MKTP: 'Marketplace',
    PAGSEGURO: 'PagSeguro',
    PAYPAL: 'PayPal',
    PICPAY: 'PicPay',
    SHEIN: 'Shein',
    SHOPEE: 'Shopee',
    TEMU: 'Temu',
};

/**
 * Nome canônico do marketplace com que a descrição começa, ou null se ela não começa
 * com um. "SHOPEE *LOJA", "Shopee.SHOPEE.K" e "SHPP*X" dão todos `SHOPEE` — é a chave
 * que agrupa as compras da mesma casa no consenso do histórico (ADR 0143).
 */
export function marketplaceNameOf(description: string): string | null {
    const tokens = normalizeAutoCategorizationText(description).split(' ').filter(Boolean);
    const prefixLength = marketplacePrefixLength(tokens);
    if (prefixLength === 0) return null;
    const name = tokens.slice(0, prefixLength).join('');
    return MARKETPLACE_ALIASES[name] ?? name;
}

/** Nome do marketplace como a pessoa lê na tela ("SHOPEE" → "Shopee"). */
export function marketplaceDisplayName(name: string): string {
    return MARKETPLACE_DISPLAY_NAMES[name] ?? name.charAt(0) + name.slice(1).toLowerCase();
}

/**
 * Ramo de regra (ou descrição normalizada) que só nomeia o marketplace — `SHOPEE*`,
 * `MERCADO*LIVRE*`, "SHOPEE SHOPEE K", ou com uma "loja" curta demais pra identificar
 * alguém (`MERCADOLIVRE*MU*`). É o tipo de regra que o aprendizado criava e que
 * categoriza toda compra do marketplace igual.
 */
export function isMarketplaceOnlyBranch(branch: string): boolean {
    const tokens = branch.split(/[*\s]+/).filter(Boolean);
    const prefixLength = marketplacePrefixLength(tokens);
    if (prefixLength === 0) return false;
    return !tokens.slice(prefixLength).some(isSellerToken);
}

/**
 * Tokens de uma descrição pra comparar com outras (sugestão por lançamentos
 * parecidos, ADR 0133): normalizada, sem sufixo de parcela, sem conectivos, sem
 * código de transação (ADR 0142) e sem repetição.
 */
export function descriptionSimilarityTokens(description: string): string[] {
    const tokens = normalizeAutoCategorizationText(stripInstallmentSuffix(description || ''))
        .split(' ')
        .filter((token) => token.length >= 2 && !CONNECTOR_TOKENS.has(token) && !isCodeToken(token));
    return [...new Set(tokens)];
}

function stripAccents(value: string): string {
    return value.normalize('NFD').replace(COMBINING_MARKS_REGEX, '');
}

function escapeRegex(value: string): string {
    return value.replace(REGEX_META_REGEX, '\\$&');
}

export function isAutoCategorizationTargetType(value: string): value is AutoCategorizationTargetType {
    return value === 'expense' || value === 'income';
}

export function normalizeAutoCategorizationText(value: string): string {
    return stripAccents(value)
        .toUpperCase()
        .replace(DESCRIPTION_NOISE_REGEX, ' ')
        .trim()
        .replace(MULTISPACE_REGEX, ' ')
        .replace(IFOOD_SHORT_PREFIX_REGEX, '$1IFOOD');
}

/**
 * Descrição sem nenhum token que identifique o gasto — "PIX ENVIADO", "PIX
 * RECEBIDO", "TRANSFERENCIA ENVIADA", "PAGAMENTO". Nela o histórico da descrição
 * exata não aponta pra UM gasto, e a categoria vem do contexto (ADR 0131).
 */
export function isLowSignalDescription(description: string): boolean {
    const tokens = normalizeAutoCategorizationText(description).split(' ').filter(Boolean);
    if (tokens.length === 0) return false;
    return tokens.every(
        (token) => LOW_SIGNAL_TOKENS.has(token) || CONNECTOR_TOKENS.has(token) || DIGITS_ONLY_REGEX.test(token),
    );
}

export function normalizeAutoCategorizationPattern(value: string): string {
    return stripAccents(value)
        .toUpperCase()
        .replace(PATTERN_NOISE_REGEX, ' ')
        .trim()
        .replace(MULTISPACE_REGEX, ' ')
        .replace(/\s*\|\s*/g, '|')
        .replace(/\s*\*\s*/g, '*')
        .replace(/\|{2,}/g, '|')
        .replace(/\*{2,}/g, '*')
        .replace(/^\|+|\|+$/g, '')
        .replace(IFOOD_SHORT_PREFIX_REGEX, '$1IFOOD');
}

export function buildBranchRegex(branch: string): RegExp {
    const escapedParts = branch.split('*').map((part) => escapeRegex(part));
    return new RegExp(`^${escapedParts.join('.*')}$`);
}

function getBranchSpecificity(branch: string): number {
    const literal = branch.replace(/\*/g, '');
    const literalLength = literal.replace(/\s+/g, '').length;
    const tokenCount = literal.split(' ').filter(Boolean).length;
    const wildcardCount = (branch.match(/\*/g) ?? []).length;
    const exactBonus = wildcardCount === 0 ? 10000 : 0;
    const prefixBonus = branch.startsWith('*') ? 0 : 400;
    const suffixBonus = branch.endsWith('*') ? 0 : 200;

    return exactBonus + (literalLength * 20) + (tokenCount * 100) + prefixBonus + suffixBonus - (wildcardCount * 5);
}

export function buildLearnedAutoCategorizationPattern(description: string): string {
    const normalizedDescription = normalizeAutoCategorizationText(stripInstallmentSuffix(description || ''));
    const tokens = normalizedDescription.split(' ').filter(Boolean);
    if (tokens.length === 0) return '';

    // Compra em marketplace só ensina com o nome da loja: `SHOPEE*LOJA*`. Sem loja
    // legível, não aprende nada — o histórico da descrição exata continua valendo.
    // Código de transação não é loja: "Amazon.com.br*2X9Y8Z" cai aqui (ADR 0142).
    const marketplaceLength = marketplacePrefixLength(tokens);
    if (marketplaceLength > 0) {
        const seller = tokens.slice(marketplaceLength).find(isSellerToken);
        return seller ? `${tokens.slice(0, marketplaceLength).join('*')}*${seller}*` : '';
    }

    const anchors: string[] = [];
    const seen = new Set<string>();
    let lowSignalFallback: string | null = null;

    for (const token of tokens) {
        if (seen.has(token) || DIGITS_ONLY_REGEX.test(token) || token.length < 2) continue;
        if (CONNECTOR_TOKENS.has(token) || isCodeToken(token)) continue;

        if (LOW_SIGNAL_TOKENS.has(token)) {
            if (anchors.length > 0) continue;
            lowSignalFallback = lowSignalFallback ?? token;
            continue;
        }

        anchors.push(token);
        seen.add(token);

        if (anchors.length >= 2) break;
    }

    // Sem âncora e sem nada além de código ("HMCT8N"), não há o que aprender: uma regra
    // com o código dentro só casaria esta compra, que o histórico exato já resolve.
    if (anchors.length === 0) {
        const fallback = lowSignalFallback
            ?? tokens.find((token) => !DIGITS_ONLY_REGEX.test(token) && !isCodeToken(token));
        return fallback ? `${fallback}*` : '';
    }

    if (anchors.length === 1) {
        return `${anchors[0]}*`;
    }

    return `${anchors[0]}*${anchors[1]}*`;
}

export function findBestAutoCategorizationRule<T extends MatchableAutoCategorizationRule>(
    description: string,
    rules: T[],
    options?: { transactionType?: AutoCategorizationTargetType },
): AutoCategorizationRuleMatch<T> | null {
    const normalizedDescription = normalizeAutoCategorizationText(description);
    if (normalizedDescription.length < 2) return null;

    let bestMatch: AutoCategorizationRuleMatch<T> | null = null;

    for (const rule of rules) {
        if (options?.transactionType && rule.categoryType && rule.categoryType !== options.transactionType) {
            continue;
        }

        const normalizedPattern = normalizeAutoCategorizationPattern(rule.matchPattern);
        if (!normalizedPattern) continue;

        const branches = normalizedPattern
            .split('|')
            .map((branch) => branch.trim())
            .filter(Boolean);

        let bestBranchMatch: Pick<AutoCategorizationRuleMatch<T>, 'matchedBranch' | 'specificity'> | null = null;

        for (const branch of branches) {
            if (!buildBranchRegex(branch).test(normalizedDescription)) continue;
            if (cutsThroughMarketplace(branch, normalizedDescription)) continue;

            const specificity = getBranchSpecificity(branch);
            if (!bestBranchMatch || specificity > bestBranchMatch.specificity) {
                bestBranchMatch = {
                    matchedBranch: branch,
                    specificity,
                };
            }
        }

        if (!bestBranchMatch) continue;

        const candidate: AutoCategorizationRuleMatch<T> = {
            rule,
            matchedBranch: bestBranchMatch.matchedBranch,
            normalizedDescription,
            normalizedPattern,
            specificity: bestBranchMatch.specificity,
        };

        if (
            !bestMatch
            || rule.priority > bestMatch.rule.priority
            || (rule.priority === bestMatch.rule.priority && candidate.specificity > bestMatch.specificity)
        ) {
            bestMatch = candidate;
        }
    }

    return bestMatch;
}
